import React from 'react';

interface VBCableInstructionsProps {
  isVisible: boolean;
  onClose: () => void;
}

const VBCableInstructions: React.FC<VBCableInstructionsProps> = ({ isVisible, onClose }) => {
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl mx-4 max-h-[75vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">🎧 Audio Capture Setup</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          <div className="space-y-6">
            {/* Overview */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-blue-800">📋 Two Options Available</h3>
                <div className="text-xs text-blue-600">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded mr-2">Recommended: VB-Cable</span>
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">Advanced: BlackHole</span>
                </div>
              </div>
              <p className="text-blue-700 text-sm mb-3">
                <strong>VB-Cable:</strong> Simpler setup, proven to work. You'll hear audio in headphones/speakers by configuring your system output.
              </p>
              <p className="text-blue-700 text-sm">
                <strong>BlackHole:</strong> Free, macOS-native, but requires Multi-Output Device setup which can be tricky.
              </p>
            </div>

            {/* Step 1: Download */}
            <div className="border rounded-lg p-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-green-800 mb-3">Option 1: VB-Cable (Recommended - Easier Setup)</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Download VB-Cable from: <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://vb-audio.com/Cable/</a></li>
                  <li>Install and restart your computer</li>
                  <li>In Google Meet: Set Speaker to <strong>"CABLE Input"</strong></li>
                  <li>In Lushin: Select <strong>"CABLE Output"</strong> as microphone</li>
                  <li><strong>To hear call audio (Method 1 - Audio MIDI Setup):</strong>
                    <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                      <li>Open <strong>Audio MIDI Setup</strong> (Applications → Utilities)</li>
                      <li>Find <strong>"VB-Cable"</strong> in the device list</li>
                      <li>Right-click it → <strong>"Use This Device for Sound Output"</strong></li>
                      <li>Set System Preferences → Sound → Output to your speakers</li>
                    </ul>
                  </li>
                  <li><strong>Alternative Method 2 - SoundSource/SpeakerAmp:</strong>
                    <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                      <li>Download SoundSource app (free trial) or use built-in apps</li>
                      <li>Route VB-Cable output to your speakers</li>
                    </ul>
                  </li>
                  <li><strong>Simplest Method 3 - Multi-Output Device:</strong>
                    <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                      <li>Audio MIDI Setup → Create Multi-Output Device</li>
                      <li>Check both VB-Cable and your speakers</li>
                      <li>Use Multi-Output Device in Google Meet instead of CABLE Input</li>
                    </ul>
                  </li>
                </ol>
                <div className="bg-green-100 border border-green-300 rounded p-2 mt-2">
                  <p className="text-green-800 text-xs">
                    <strong>✅ This works reliably!</strong> You'll capture customer audio in Lushin and hear it through your speakers.
                  </p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-3">Option 2: BlackHole (Advanced - Free but Complex)</h3>
                <p className="text-blue-700 text-sm mb-3">Only use this if you want a free solution and don't mind complex setup:</p>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Visit: <a href="https://github.com/ExistentialAudio/BlackHole" target="_blank" rel="noopener noreferrer" 
                     className="text-blue-600 hover:underline">https://github.com/ExistentialAudio/BlackHole</a></li>
                  <li>Download BlackHole 2ch (or use Homebrew: <code className="bg-gray-100 px-1 rounded">brew install blackhole-2ch</code>)</li>
                  <li>Run the installer and follow the instructions</li>
                  <li><strong>Important:</strong> Restart your computer after installation</li>
                </ol>
              </div>
            </div>

            {/* Step 2: Configure */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-3">Step 2: Create Multi-Output Device (macOS) - CRITICAL STEP</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm mb-4">
                <li>Open <strong>Audio MIDI Setup</strong> (Applications → Utilities, or search "Audio MIDI")</li>
                <li>Click the <strong>"+"</strong> button (bottom left) and select <strong>"Create Multi-Output Device"</strong></li>
                <li>In the Multi-Output Device window:
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>✅ Check <strong>"BlackHole 2ch"</strong></li>
                    <li>✅ Check your <strong>speakers/headphones</strong> (Built-in Output, AirPods, etc.)</li>
                    <li>Make sure BlackHole is FIRST in the list (drag to reorder if needed)</li>
                  </ul>
                </li>
                <li>⚠️ <strong>DO NOT</strong> set Multi-Output as system default yet</li>
                <li>Leave Audio MIDI Setup open for now</li>
              </ol>
              
              <h3 className="font-semibold text-gray-800 mb-3">Step 3: Test BlackHole First</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm mb-4">
                <li>In Lushin, open Audio Settings and look for <strong>"BlackHole 2ch"</strong></li>
                <li>If you don't see it:
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>Refresh the browser completely</li>
                    <li>Check browser has microphone permission</li>
                    <li>Try restarting the browser</li>
                  </ul>
                </li>
                <li>Once you see BlackHole 2ch, continue to Step 4</li>
              </ol>
              
              <h3 className="font-semibold text-gray-800 mb-3">Step 4: Configure Your Call Application</h3>
              
              {/* Zoom */}
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">🔵 For Zoom:</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm ml-4">
                  <li>Open Zoom Settings → Audio</li>
                  <li>Set Speaker: <strong>"Multi-Output Device"</strong> (the one you created)</li>
                  <li>Keep Microphone: Your physical microphone</li>
                  <li>⚠️ You might not hear audio in Zoom test - this is normal</li>
                  <li>Join a meeting to test properly</li>
                </ol>
              </div>

              {/* Teams */}
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">🟡 For Microsoft Teams:</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm ml-4">
                  <li>Open Teams Settings → Devices</li>
                  <li>Set Speaker: <strong>"Multi-Output Device"</strong></li>
                  <li>Keep Microphone: Your physical microphone</li>
                  <li>Test call to verify audio routing</li>
                </ol>
              </div>

              {/* Generic */}
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">🌐 For Google Meet:</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm ml-4">
                  <li>In Google Meet, click Settings (gear icon) → Audio</li>
                  <li>Set Speakers: <strong>"Multi-Output Device"</strong></li>
                  <li>Keep Microphone: Your physical microphone</li>
                  <li>⚠️ Meet might show "No speakers detected" - ignore this</li>
                  <li>Join the call to test audio routing</li>
                </ol>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">🔄 For Other Apps (Skype, WhatsApp, etc.):</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm ml-4">
                    <li>Find audio settings in your calling application</li>
                    <li>Set output/speaker device to "Multi-Output Device"</li>
                    <li>Keep input/microphone as your physical device</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Step 3: Lushin */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-3">Step 5: Configure Lushin</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm">
                <li>In Lushin, open the Audio Settings (click the microphone icon)</li>
                <li>For microphone device, select: <strong>"BlackHole 2ch"</strong> (should appear as an input device)</li>
                <li>If BlackHole 2ch doesn't appear, refresh the device list or restart the browser</li>
                <li>Click "Start Recording" - you'll now capture customer audio!</li>
                <li><strong>Optional:</strong> Set up dual capture by also selecting your physical microphone in a second browser tab</li>
              </ol>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                <h4 className="font-semibold text-yellow-800 text-sm mb-2">⚠️ Important Notes:</h4>
                <ul className="text-xs text-yellow-700 space-y-1">
                  <li>• BlackHole 2ch must appear as a <strong>microphone input</strong> in Lushin</li>
                  <li>• If it doesn't show up, try refreshing the browser or restarting after BlackHole installation</li>
                  <li>• Make sure your call app is outputting to the Multi-Output Device, not just BlackHole directly</li>
                </ul>
              </div>
            </div>

            {/* Troubleshooting */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800 mb-3">🔧 Troubleshooting</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <h4 className="font-medium text-yellow-700">BlackHole installed but not showing in Lushin:</h4>
                  <ul className="list-disc list-inside text-yellow-600 ml-4">
                    <li><strong>Most common:</strong> Restart your browser completely</li>
                    <li>Check System Preferences → Security & Privacy → Microphone → Enable for your browser</li>
                    <li>In Terminal, run: <code className="bg-yellow-100 px-1 rounded text-xs">sudo launchctl kickstart -k system/com.apple.audio.coreaudiod</code></li>
                    <li>Restart computer if above doesn't work</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-yellow-700">Can hear audio but Lushin not capturing:</h4>
                  <ul className="list-disc list-inside text-yellow-600 ml-4">
                    <li>Verify your call app outputs to <strong>"Multi-Output Device"</strong> (not BlackHole directly)</li>
                    <li>In Lushin, select <strong>"BlackHole 2ch"</strong> as microphone</li>
                    <li>Check Audio MIDI Setup: BlackHole should be FIRST in Multi-Output Device list</li>
                    <li>Try speaking into the call - check if Lushin shows audio waveforms</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-yellow-700">Can't hear customer audio:</h4>
                  <ul className="list-disc list-inside text-yellow-600 ml-4">
                    <li>In Audio MIDI Setup, ensure your speakers/headphones are ✅ checked in Multi-Output Device</li>
                    <li>Try checking/unchecking your speakers in the Multi-Output Device</li>
                    <li>Verify call app is outputting to Multi-Output Device, not BlackHole directly</li>
                    <li><strong>Last resort:</strong> Use VB-Cable instead (simpler setup, proven to work)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Advanced Setup */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-3">🚀 Advanced: Dual Audio Setup</h3>
              <div className="text-sm space-y-2">
                <p><strong>For complete dual audio capture (salesperson + customer):</strong></p>
                <ol className="list-decimal list-inside space-y-1 ml-4">
                  <li>Set up BlackHole Multi-Output Device as above for customer audio</li>
                  <li>Open Lushin in two browser tabs/windows</li>
                  <li><strong>Tab 1:</strong> Set device to "BlackHole 2ch" (customer audio)</li>
                  <li><strong>Tab 2:</strong> Set device to your physical microphone (salesperson audio)</li>
                  <li>Start recording in both tabs</li>
                </ol>
                <p className="text-gray-600 italic">
                  💡 The AI will automatically detect objections from customer audio and monitor speech rate from salesperson audio.
                </p>
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-2">✅ Benefits of BlackHole Setup</h3>
              <ul className="list-disc list-inside text-green-700 text-sm space-y-1">
                <li><strong>Free & Open Source:</strong> No licensing fees or restrictions</li>
                <li><strong>macOS Native:</strong> Built specifically for macOS, very stable</li>
                <li><strong>Multi-Output Support:</strong> Hear audio while capturing it</li>
                <li><strong>High Quality:</strong> Zero latency, no compression or quality loss</li>
                <li><strong>Universal Compatibility:</strong> Works with any calling application</li>
                <li><strong>Privacy:</strong> All audio processing stays on your computer</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors"
            >
              Got it! Close Instructions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VBCableInstructions;